import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { STAGE_LABEL, STAGE_BADGE } from "@/constants/roles";
import { toast } from "sonner";
import { PoSortHeader, sortByPo } from "@/components/PoSortHeader";
import { useT } from "@/i18n/LanguageContext";
import { WithdrawnRefundsSection, LostRefundsDialog } from "@/components/RefundRecovery";

const BADGE_CLS = {
  neutral: "bg-slate-100 text-slate-700 border-slate-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  critical: "bg-red-50 text-red-700 border-red-200",
};

// Human-friendly factory labels & accent colors
const FACTORY_META = {
  factory_1: { label: "Factory 1", accent: "#C30000", tone: "bg-red-50 border-red-200 text-red-800" },
  factory_2: { label: "Factory 2", accent: "#1E40AF", tone: "bg-blue-50 border-blue-200 text-blue-800" },
  factory_3: { label: "Factory 3", accent: "#166534", tone: "bg-emerald-50 border-emerald-200 text-emerald-800" },
};

const fmt$ = (n) => `¥${Number(n || 0).toFixed(2)}`;

export default function Billing() {
  const { user } = useAuth();
  const { t } = useT();
  const qc = useQueryClient();
  // Factory users see refunds waiting for their approval.
  // Back Office see the refund requests they created.
  // Super Admin sees everything and can pay batches.
  const isSuperAdmin = user.role === "super_admin";
  const isBackOffice = user.role === "back_office";
  const isFactory = ["factory_1", "factory_2", "factory_3"].includes(user.role);
  const allowed = isSuperAdmin || isBackOffice || isFactory;
  const { data = [] } = useQuery({
    queryKey: ["batches"],
    queryFn: async () => (await api.get("/batches")).data,
    enabled: allowed && (isSuperAdmin || user.role === "factory_1"),
  });
  const { data: refunds = [] } = useQuery({
    queryKey: ["refunds"],
    queryFn: async () => (await api.get("/refunds")).data,
    enabled: allowed,
  });
  const [openBatchId, setOpenBatchId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set()); // batch ids
  const [selectedRefundIds, setSelectedRefundIds] = useState(() => new Set()); // refund ids
  const [refundOrder, setRefundOrder] = useState(null); // { order, batch } — opens request modal
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  // "Refund Any Order" launcher — search by PO/VIN/catalog, pick order, then use the refund modal.
  const [orderPickerOpen, setOrderPickerOpen] = useState(false);
  const [orderQuery, setOrderQuery] = useState("");
  const orderSearch = useQuery({
    queryKey: ["orderSearch", orderQuery],
    queryFn: async () => (await api.get(`/orders?search=${encodeURIComponent(orderQuery)}`)).data,
    enabled: orderPickerOpen && orderQuery.trim().length >= 2,
  });

  const bulkPay = useMutation({
    mutationFn: ({ batch_ids, refund_ids }) => api.post("/batches/bulk-mark-paid", { batch_ids, refund_ids }),
    onSuccess: (res) => {
      const d = res?.data || {};
      const parts = [];
      if (d.marked_paid) parts.push(`${d.marked_paid} batch(es) paid`);
      if (d.refunds_applied) parts.push(`${d.refunds_applied} refund(s) applied`);
      toast.success(`${parts.join(" · ") || "Nothing to update"} · Net ${fmt$(d.total_amount)}`);
      setSelectedIds(new Set());
      setSelectedRefundIds(new Set());
      qc.invalidateQueries();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Bulk payment failed"),
  });

  const paySingle = useMutation({
    mutationFn: (id) => api.post(`/batches/${id}/mark-paid`),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Batch marked as paid"); },
  });

  const createRefund = useMutation({
    mutationFn: ({ order_id, amount, reason }) => api.post("/refunds", { order_id, amount: parseFloat(amount), reason }),
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success("Refund request submitted — awaiting factory approval");
      setRefundOrder(null);
      setRefundAmount("");
      setRefundReason("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to submit refund"),
  });

  const approveRefund = useMutation({
    mutationFn: (rid) => api.post(`/refunds/${rid}/approve`),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Refund approved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const rejectRefund = useMutation({
    mutationFn: (rid) => api.post(`/refunds/${rid}/reject`),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Refund rejected"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const deleteRefund = useMutation({
    mutationFn: (rid) => api.delete(`/refunds/${rid}`),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Refund request withdrawn — it is kept under “Withdrawn refund requests” and can be restored"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const restoreRefund = useMutation({
    mutationFn: (rid) => api.post(`/refunds/${rid}/restore`),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Refund request restored — back in the pending list"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const [lostOpen, setLostOpen] = useState(false);

  // Group batches by factory. Only render sections that actually have batches
  // so that when more factories come online, the sections auto-appear.
  const grouped = useMemo(() => {
    const g = {};
    for (const b of data) {
      const key = b.factory || "factory_1";
      g[key] = g[key] || [];
      g[key].push(b);
    }
    return g;
  }, [data]);

  // Split refunds into buckets
  const refundBuckets = useMemo(() => {
    const pending = refunds.filter((r) => r.status === "pending");
    const withdrawn = refunds.filter((r) => r.status === "withdrawn");
    const approvedUnapplied = refunds.filter((r) => r.status === "approved" && !r.applied);
    const applied = refunds.filter((r) => r.applied);
    return { pending, withdrawn, approvedUnapplied, applied };
  }, [refunds]);

  // Group approved refunds by factory so they mix into each factory section
  const refundsByFactory = useMemo(() => {
    const g = {};
    for (const r of refundBuckets.approvedUnapplied) {
      const key = r.factory || "factory_1";
      g[key] = g[key] || [];
      g[key].push(r);
    }
    return g;
  }, [refundBuckets.approvedUnapplied]);

  // Derived calculator totals: batches (positive) − refunds (negative) = net
  const calc = useMemo(() => {
    const chosenBatches = data.filter((b) => selectedIds.has(b.id));
    const chosenRefunds = refundBuckets.approvedUnapplied.filter((r) => selectedRefundIds.has(r.id));
    const batchTotal = chosenBatches.reduce((s, b) => s + Number(b.amount || 0), 0);
    const refundTotal = chosenRefunds.reduce((s, r) => s + Number(r.amount || 0), 0);
    return {
      chosenBatches,
      chosenRefunds,
      batchTotal,
      refundTotal,
      net: batchTotal - refundTotal,
      count: chosenBatches.length + chosenRefunds.length,
    };
  }, [data, selectedIds, refundBuckets.approvedUnapplied, selectedRefundIds]);

  if (!allowed) {
    return <div className="text-sm text-slate-500">Billing is available to Factory, Back Office and Super Admin only.</div>;
  }

  const openBatch = data.find((b) => b.id === openBatchId);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    // Approved refunds deducted from this bill follow the bill's selection.
    const attached = refundBuckets.approvedUnapplied.filter((r) => r.bill_batch_id === id).map((r) => r.id);
    if (attached.length) setSelectedRefundIds((prev) => {
      const next = new Set(prev);
      const on = !selectedIds.has(id);
      attached.forEach((rid) => (on ? next.add(rid) : next.delete(rid)));
      return next;
    });
  };
  const toggleRefund = (id) => {
    setSelectedRefundIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const factoryOrder = ["factory_1", "factory_2", "factory_3"];

  return (
    <div className={`space-y-6 ${calc.count > 0 && !openBatch ? "pb-32" : ""}`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{t("Billing")}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {openBatch
              ? "Spreadsheet view — click a price to edit, or request a refund on any line."
              : "Batches are grouped per factory. Tick boxes to select batches (positive) and approved refunds (negative), then mark them all paid at once as a single net wire."}
          </p>
        </div>
        {openBatch && (
          <Button variant="outline" data-testid="billing-back" onClick={() => setOpenBatchId(null)}>← Back to batches</Button>
        )}
      </div>

      {!openBatch ? (
        <>
          {/* Refund Any Order — Back Office / Super Admin can create a refund
              request against any order, without having to open its batch. */}
          {(isSuperAdmin || isBackOffice) && (
            <div className="bg-white border border-slate-200 rounded-md px-4 py-3 flex items-center justify-between" data-testid="refund-any-order-bar">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Refund a specific order</div>
                <div className="text-sm text-slate-700">Search any order by PO # or VIN and file a refund request without opening its batch.</div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" data-testid="find-lost-refunds-btn" onClick={() => setLostOpen(true)}>
                  Find lost requests
                </Button>
                <Button
                  data-testid="open-order-picker-btn"
                  onClick={() => { setOrderPickerOpen(true); setOrderQuery(""); }}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  + Refund by PO #
                </Button>
              </div>
            </div>
          )}
          {(isSuperAdmin || isBackOffice) && <LostRefundsDialog open={lostOpen} onOpenChange={setLostOpen} />}

          {/* Pending refund requests — shown at top for immediate attention */}
          {refundBuckets.pending.length > 0 && (
            <PendingRefundsSection
              refunds={refundBuckets.pending}
              user={user}
              isFactory={isFactory}
              isBackOffice={isBackOffice}
              isSuperAdmin={isSuperAdmin}
              onApprove={(rid) => approveRefund.mutate(rid)}
              onReject={(rid) => rejectRefund.mutate(rid)}
              onDelete={(rid) => {
                const r = refunds.find((x) => x.id === rid);
                if (window.confirm(`Withdraw the refund request for ${r?.po_number || "this order"} (${fmt$(r?.amount)})?\n\nIt will leave the pending list. You can restore it later from “Withdrawn refund requests”.`)) deleteRefund.mutate(rid);
              }}
            />
          )}
          {(isSuperAdmin || isBackOffice) && (
            <WithdrawnRefundsSection refunds={refundBuckets.withdrawn} onRestore={(rid) => restoreRefund.mutate(rid)} busy={restoreRefund.isPending} />
          )}

          {(isSuperAdmin || user.role === "factory_1") && factoryOrder.map((fkey) => {
            const factoryBatches = grouped[fkey] || [];
            const factoryRefunds = refundsByFactory[fkey] || [];
            if (factoryBatches.length === 0 && factoryRefunds.length === 0) return null;
            return (
              <FactoryBatchGroup
                key={fkey}
                factoryKey={fkey}
                batches={factoryBatches}
                refunds={factoryRefunds}
                onOpen={setOpenBatchId}
                isSuperAdmin={isSuperAdmin}
                selectedIds={selectedIds}
                selectedRefundIds={selectedRefundIds}
                onToggle={toggle}
                onToggleRefund={toggleRefund}
                onPay={(id) => paySingle.mutate(id)}
              />
            );
          })}

          {/* Applied refunds — history */}
          {refundBuckets.applied.length > 0 && (isSuperAdmin || user.role === "factory_1") && (
            <AppliedRefundsSection refunds={refundBuckets.applied} />
          )}

          {(isSuperAdmin || user.role === "factory_1") && data.length === 0 && refunds.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-md px-4 py-10 text-center text-slate-400 text-sm" data-testid="batches-empty">
              No batches yet.
            </div>
          )}
          {isBackOffice && refunds.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-md px-4 py-10 text-center text-slate-400 text-sm">
              You have no refund requests. Open any completed order to request one.
            </div>
          )}

          {/* Sticky Calculator Bar */}
          {isSuperAdmin && calc.count > 0 && (
            <div
              className="fixed bottom-0 left-0 right-0 md:left-56 z-40 border-t bg-white shadow-[0_-4px_16px_rgba(0,0,0,0.08)]"
              data-testid="billing-calculator-bar"
            >
              <div className="max-w-full px-6 py-4 flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-6 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Selected</div>
                    <div className="font-display text-xl font-bold" data-testid="calc-selected-count">
                      {calc.chosenBatches.length} batch{calc.chosenBatches.length === 1 ? "" : "es"}
                      {calc.chosenRefunds.length > 0 && (
                        <span className="text-red-700 ml-2">· {calc.chosenRefunds.length} refund{calc.chosenRefunds.length === 1 ? "" : "s"}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Batches</div>
                      <div className="font-mono text-base text-slate-700" data-testid="calc-batch-total">{fmt$(calc.batchTotal)}</div>
                    </div>
                    {calc.refundTotal > 0 && (
                      <>
                        <div className="text-slate-400 text-xl">−</div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest font-mono text-red-700">Refunds</div>
                          <div className="font-mono text-base text-red-700" data-testid="calc-refund-total">{fmt$(calc.refundTotal)}</div>
                        </div>
                        <div className="text-slate-400 text-xl">=</div>
                      </>
                    )}
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-mono text-emerald-700">Net Total</div>
                      <div className="font-display text-2xl font-bold text-slate-900" data-testid="calc-total-amount">
                        {fmt$(calc.net)}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => { setSelectedIds(new Set()); setSelectedRefundIds(new Set()); }}
                    data-testid="calc-clear-btn"
                  >
                    Clear
                  </Button>
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={bulkPay.isPending}
                    onClick={() => bulkPay.mutate({ batch_ids: Array.from(selectedIds), refund_ids: Array.from(selectedRefundIds) })}
                    data-testid="calc-bulk-pay-btn"
                  >
                    {bulkPay.isPending
                      ? "Marking..."
                      : `✓ Mark ${calc.count} item${calc.count === 1 ? "" : "s"} Paid · Net ${fmt$(calc.net)}`}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <BatchSpreadsheet
          batch={openBatch}
          isSuperAdmin={isSuperAdmin}
          isBackOffice={isBackOffice}
          isFactory={isFactory}
          onPay={() => paySingle.mutate(openBatch.id)}
          onRequestRefund={(order) => {
            setRefundOrder({ order, batch: openBatch });
            setRefundAmount(String(order.price ?? 650));
            setRefundReason("");
          }}
        />
      )}

      {/* Refund request modal (Super Admin / Back Office trigger it from BatchSpreadsheet) */}
      <Dialog open={!!refundOrder} onOpenChange={(v) => !v && setRefundOrder(null)}>
        <DialogContent data-testid="refund-modal">
          <DialogHeader>
            <DialogTitle>Request Refund · {refundOrder?.order?.po_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              This creates a pending refund request. <span className="font-semibold">Factory 1 must approve</span> it before it can be
              deducted from a future payment. Requested by:{" "}
              <span className="font-semibold">{user?.name || user?.email}</span>.
            </p>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Amount (¥ RMB)</label>
              <Input
                data-testid="refund-amount-input"
                type="number"
                min="0.01"
                step="0.01"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reason (optional)</label>
              <Textarea
                data-testid="refund-reason-input"
                rows={3}
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder="e.g. Cover damaged in transit, customer returned within 14 days…"
              />
            </div>
            <Button
              data-testid="refund-submit-btn"
              className="w-full bg-red-600 hover:bg-red-700 text-white"
              disabled={!refundAmount || parseFloat(refundAmount) <= 0 || createRefund.isPending}
              onClick={() => createRefund.mutate({ order_id: refundOrder.order.id, amount: refundAmount, reason: refundReason })}
            >
              {createRefund.isPending ? "Submitting..." : "Submit Refund Request"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Picker — refund any order without opening a batch */}
      <Dialog open={orderPickerOpen} onOpenChange={(v) => !v && setOrderPickerOpen(false)}>
        <DialogContent data-testid="order-picker-modal">
          <DialogHeader><DialogTitle>Refund — Find an Order</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input
              data-testid="order-picker-search"
              placeholder="Type PO number, VIN, catalog #, or brand…"
              value={orderQuery}
              onChange={(e) => setOrderQuery(e.target.value)}
              autoFocus
            />
            <div className="max-h-72 overflow-y-auto border border-slate-200 rounded" data-testid="order-picker-results">
              {orderQuery.trim().length < 2 && (
                <div className="px-4 py-8 text-center text-slate-400 text-xs">Type at least 2 characters to search</div>
              )}
              {orderQuery.trim().length >= 2 && orderSearch.isLoading && (
                <div className="px-4 py-8 text-center text-slate-400 text-xs">Searching…</div>
              )}
              {orderQuery.trim().length >= 2 && !orderSearch.isLoading && (orderSearch.data || []).length === 0 && (
                <div className="px-4 py-8 text-center text-slate-400 text-xs">No orders match "{orderQuery}"</div>
              )}
              {(orderSearch.data || []).slice(0, 20).map((o) => {
                const meta = FACTORY_META[o.assigned_factory] || FACTORY_META.factory_1;
                return (
                  <button
                    key={o.id}
                    className="w-full text-left px-3 py-2 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 flex items-center justify-between gap-3"
                    data-testid={`order-picker-result-${o.id}`}
                    onClick={() => {
                      // Move to refund modal for that order
                      setOrderPickerOpen(false);
                      setRefundOrder({
                        order: { id: o.id, po_number: o.po_number, price: null },
                        batch: { factory: o.assigned_factory || "factory_1" },
                      });
                      setRefundAmount("");
                      setRefundReason("");
                    }}
                  >
                    <div>
                      <div className="font-mono font-semibold text-slate-900">{o.po_number}</div>
                      <div className="text-xs text-slate-500">{[o.brand, o.model, o.year].filter(Boolean).join(" · ") || "—"}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`border text-[10px] ${meta.tone}`}>{meta.label}</Badge>
                      <span className="text-[10px] font-mono text-slate-500 uppercase">{STAGE_LABEL[o.status] || o.status}</span>
                      {o.cancelled && <Badge className="bg-red-100 text-red-800 border border-red-300 text-[10px]">✕ REFUNDED</Badge>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------- Pending Refunds Section --------
function PendingRefundsSection({ refunds, user, isFactory, isBackOffice, isSuperAdmin, onApprove, onReject, onDelete }) {
  const [poSort, setPoSort] = useState(null);
  const sortedRefunds = useMemo(() => (poSort ? sortByPo(refunds, poSort) : refunds), [refunds, poSort]);
  // Factory users only see refunds for their factory (server already filters).
  return (
    <section className="bg-amber-50/40 border border-amber-200 rounded-md overflow-hidden" data-testid="pending-refunds-section">
      <div className="px-5 py-3 border-b border-amber-200 flex items-center justify-between bg-amber-50">
        <div className="flex items-baseline gap-3">
          <div className="text-[10px] uppercase tracking-widest font-mono opacity-70 text-amber-900">Pending refund requests</div>
          <div className="font-display text-lg font-bold text-amber-900">
            {refunds.length} awaiting approval
          </div>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-white/60">
          <tr>
            <th className="px-4 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              <PoSortHeader sort={poSort} onChange={setPoSort} testid="pending-refund-po-sort" />
            </th>
            {["Factory", "Amount", "Reason", "Requested by", "When", "Action"].map((h) => (
              <th key={h} className="px-4 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRefunds.map((r) => {
            const canApprove = (isFactory && user.role === "factory_1");
            const canDelete = (isSuperAdmin || (isBackOffice && r.requested_by_role === "back_office"));
            const meta = FACTORY_META[r.factory] || FACTORY_META.factory_1;
            return (
              <tr key={r.id} className="border-t border-amber-100" data-testid={`pending-refund-${r.id}`}>
                <td className="px-4 py-3 font-mono font-semibold">{r.po_number || "—"}</td>
                <td className="px-4 py-3">
                  <Badge className={`border ${meta.tone}`}>{meta.label}</Badge>
                </td>
                <td className="px-4 py-3 font-mono text-red-700 font-semibold">−{fmt$(r.amount)}{r.bill_batch_date && <div className="text-[10px] font-sans font-normal text-slate-500" data-testid={`pending-refund-bill-${r.id}`}>from bill {r.bill_batch_date}</div>}</td>
                <td className="px-4 py-3 text-slate-600 text-xs max-w-xs truncate" title={r.reason}>{r.reason || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{r.requested_by}</td>
                <td className="px-4 py-3 text-xs font-mono text-slate-500">{r.requested_at?.slice(0, 16).replace("T", " ")}</td>
                <td className="px-4 py-3 space-x-2">
                  {canApprove && (
                    <>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onApprove(r.id)} data-testid={`approve-refund-${r.id}`}>
                        ✓ Approve
                      </Button>
                      <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => onReject(r.id)} data-testid={`reject-refund-${r.id}`}>
                        ✕ Reject
                      </Button>
                    </>
                  )}
                  {canDelete && !canApprove && (
                    <Button size="sm" variant="outline" onClick={() => onDelete(r.id)} data-testid={`delete-refund-${r.id}`}>
                      Withdraw
                    </Button>
                  )}
                  {!canApprove && !canDelete && <span className="text-xs text-slate-400">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function AppliedRefundsSection({ refunds }) {
  const [poSort, setPoSort] = useState(null);
  const sortedRefunds = useMemo(() => (poSort ? sortByPo(refunds, poSort) : refunds), [refunds, poSort]);
  const total = refunds.reduce((s, r) => s + Number(r.amount || 0), 0);
  return (
    <section className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="applied-refunds-section">
      <div className="px-5 py-3 border-b bg-slate-50 flex items-center gap-3">
        <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Refunds — history</div>
        <div className="font-display text-lg font-bold text-slate-700">
          {refunds.length} applied · {fmt$(total)} deducted
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-4 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              <PoSortHeader sort={poSort} onChange={setPoSort} testid="applied-refund-po-sort" />
            </th>
            {["Factory", "Amount", "Reason", "Approved by", "Applied"].map((h) => (
              <th key={h} className="px-4 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRefunds.map((r) => {
            const meta = FACTORY_META[r.factory] || FACTORY_META.factory_1;
            return (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`applied-refund-${r.id}`}>
                <td className="px-4 py-3 font-mono">{r.po_number || "—"}</td>
                <td className="px-4 py-3"><Badge className={`border ${meta.tone}`}>{meta.label}</Badge></td>
                <td className="px-4 py-3 font-mono text-red-700">−{fmt$(r.amount)}</td>
                <td className="px-4 py-3 text-xs text-slate-500 max-w-xs truncate" title={r.reason}>{r.reason || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{r.approved_by || "—"}</td>
                <td className="px-4 py-3 text-xs font-mono text-slate-500">{r.applied_at?.slice(0, 16).replace("T", " ") || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function FactoryBatchGroup({ factoryKey, batches, refunds = [], onOpen, isSuperAdmin, selectedIds, selectedRefundIds, onToggle, onToggleRefund, onPay }) {
  const meta = FACTORY_META[factoryKey] || { label: factoryKey, accent: "#334155", tone: "bg-slate-50 border-slate-200 text-slate-800" };
  const unpaid = batches.filter((b) => !b.paid);
  const groupTotal = batches.reduce((s, b) => s + Number(b.amount || 0), 0);
  const groupUnpaid = unpaid.reduce((s, b) => s + Number(b.amount || 0), 0);
  const refundTotal = refunds.reduce((s, r) => s + Number(r.amount || 0), 0);

  // Select-all-in-group toggle
  const allUnpaidSelected = unpaid.length > 0 && unpaid.every((b) => selectedIds.has(b.id));
  const allRefundsSelected = refunds.length > 0 && refunds.every((r) => selectedRefundIds.has(r.id));
  const allSelectableSelected =
    (unpaid.length === 0 || allUnpaidSelected) && (refunds.length === 0 || allRefundsSelected) && (unpaid.length + refunds.length > 0);
  const toggleAll = () => {
    if (allSelectableSelected) {
      unpaid.forEach((b) => selectedIds.has(b.id) && onToggle(b.id));
      refunds.forEach((r) => selectedRefundIds.has(r.id) && onToggleRefund(r.id));
    } else {
      unpaid.forEach((b) => !selectedIds.has(b.id) && onToggle(b.id));
      refunds.forEach((r) => !selectedRefundIds.has(r.id) && onToggleRefund(r.id));
    }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={`factory-group-${factoryKey}`}>
      {/* Factory header band */}
      <div className={`flex items-center justify-between px-5 py-3 border-b ${meta.tone}`}>
        <div className="flex items-baseline gap-3">
          <div className="text-[10px] uppercase tracking-widest font-mono opacity-70">Factory Billing</div>
          <div className="font-display text-xl font-bold" style={{ color: meta.accent }}>{meta.label}</div>
          <div className="text-xs font-mono opacity-80">
            {batches.length} batch{batches.length === 1 ? "" : "es"} · {fmt$(groupTotal)} total
            {unpaid.length > 0 && (
              <> · <span className="font-semibold">{fmt$(groupUnpaid)} unpaid</span></>
            )}
            {refunds.length > 0 && (
              <> · <span className="font-semibold text-red-700">−{fmt$(refundTotal)} refund{refunds.length === 1 ? "" : "s"}</span></>
            )}
          </div>
        </div>
        {isSuperAdmin && (unpaid.length > 0 || refunds.length > 0) && (
          <label className="flex items-center gap-2 text-xs cursor-pointer select-none" data-testid={`select-all-${factoryKey}`}>
            <Checkbox checked={allSelectableSelected} onCheckedChange={toggleAll} />
            <span>Select all unpaid</span>
          </label>
        )}
      </div>

      <table className="w-full text-sm">
        <thead className="bg-slate-50">
          <tr>
            {isSuperAdmin && <th className="px-4 py-3 w-10"></th>}
            {["Date", "Type", "Total", "Status", ""].map((h) => (
              <th key={h} className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {batches.map((b) => {
            const disabled = b.paid;
            const checked = selectedIds.has(b.id);
            return (
              <tr key={b.id} className={`border-t border-slate-100 ${checked ? "bg-amber-50/60" : "hover:bg-slate-50"}`} data-testid={`batch-row-${b.id}`}>
                {isSuperAdmin && (
                  <td className="px-4 py-3">
                    <Checkbox
                      checked={checked}
                      disabled={disabled}
                      onCheckedChange={() => onToggle(b.id)}
                      data-testid={`batch-select-${b.id}`}
                      aria-label={disabled ? "Batch already paid" : "Select batch for bulk payment"}
                    />
                  </td>
                )}
                <td className="px-4 py-3 font-display font-bold text-base">{b.batch_date}{b.kind === "stock" && <span className="ml-2 inline-block rounded border border-indigo-300 bg-indigo-50 px-1.5 py-0.5 text-[10px] font-sans font-semibold text-indigo-800 align-middle" data-testid={`bill-stock-badge-${b.id}`}>📦 BATCH ORDER #{b.stock_batch_no}</span>}</td>
                <td className="px-4 py-3 text-xs text-slate-500" data-testid={`bill-type-${b.id}`}>{b.kind === "stock" ? `${b.label || "📦 Batch order"} · ${(b.orders || []).length} set(s) for stock` : `Batch · ${(b.orders || []).length} order(s)`}</td>
                <td className="px-4 py-3 font-mono text-sm text-slate-700">{fmt$(b.amount)}</td>
                <td className="px-4 py-3">
                  {b.paid ? (
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border">✓ PAID {b.paid_at?.slice(0, 10)}</Badge>
                  ) : (
                    <Badge className="bg-amber-50 text-amber-700 border-amber-200 border">Awaiting payment</Badge>
                  )}
                </td>
                <td className="px-4 py-3 text-right space-x-2">
                  <Button size="sm" variant="outline" onClick={() => onOpen(b.id)} data-testid={`batch-open-${b.id}`}>Open →</Button>
                  {isSuperAdmin && !b.paid && (
                    <Button size="sm" onClick={() => onPay(b.id)} data-testid={`batch-pay-list-${b.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      ✓ Mark Paid
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
          {/* Approved refunds — negative rows */}
          {refunds.map((r) => {
            const checked = selectedRefundIds.has(r.id);
            return (
              <tr key={r.id} className={`border-t border-red-100 ${checked ? "bg-red-50/60" : "hover:bg-red-50/30"}`} data-testid={`refund-row-${r.id}`}>
                {isSuperAdmin && (
                  <td className="px-4 py-3">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => onToggleRefund(r.id)}
                      data-testid={`refund-select-${r.id}`}
                      aria-label="Select refund for deduction"
                    />
                  </td>
                )}
                <td className="px-4 py-3 text-xs font-mono text-slate-500">{r.approved_at?.slice(0, 10)}</td>
                <td className="px-4 py-3 text-xs text-red-700 font-semibold uppercase tracking-wider">
                  Refund · {r.po_number}
                </td>
                <td className="px-4 py-3 font-mono text-sm text-red-700 font-semibold">−{fmt$(r.amount)}</td>
                <td className="px-4 py-3">
                  <Badge className="bg-red-50 text-red-700 border-red-200 border">{r.bill_batch_date ? `Approved · deducted from bill ${r.bill_batch_date}` : "Approved · deduct next payment"}</Badge>
                </td>
                <td className="px-4 py-3 text-right text-xs text-slate-500 max-w-xs truncate" title={r.reason}>{r.reason || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function BatchSpreadsheet({ batch, isSuperAdmin, isBackOffice, isFactory, onPay, onRequestRefund }) {
  const qc = useQueryClient();
  const orders = useMemo(() => batch.orders || [], [batch.orders]);
  const total = orders.reduce((s, o) => s + (parseFloat(o.price) || 0), 0);
  const [editing, setEditing] = useState(null); // order_id
  const [editValue, setEditValue] = useState("");
  const [poSort, setPoSort] = useState(null);
  const sortedOrders = useMemo(() => (poSort ? sortByPo(orders, poSort) : orders), [orders, poSort]);
  const meta = FACTORY_META[batch.factory] || FACTORY_META.factory_1;
  const canRequestRefund = isSuperAdmin || isBackOffice;
  // For factory users, an order that has passed "Mark Received" at Packing is
  // the FINAL status they care about — anything from `packing` onward is
  // "Completed" from Factory 1's perspective. Rows before Mark Received keep
  // their live stage (Pending / Advance / Production / Product Transfer).
  const FACTORY_DONE_STAGES = new Set(["packing", "inventory_packing", "shipping", "completed"]);
  const statusForRow = (o) => {
    if (isFactory && !isSuperAdmin && FACTORY_DONE_STAGES.has(o.status)) {
      return { label: "Completed", badge: "success" };
    }
    return { label: STAGE_LABEL[o.status] || o.status, badge: STAGE_BADGE[o.status] };
  };
  const savePrice = useMutation({
    mutationFn: ({ order_id, price }) => api.patch(`/batches/${batch.id}/order-price`, { order_id, price: parseFloat(price) }),
    onSuccess: () => { qc.invalidateQueries(); setEditing(null); toast.success("Price updated"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const beginEdit = (o) => { setEditing(o.id); setEditValue(String(o.price ?? 650)); };
  const commit = () => savePrice.mutate({ order_id: editing, price: editValue });

  return (
    <div className="space-y-4">
      {/* Batch header */}
      <div className="bg-white border border-slate-200 rounded-md p-5 flex items-center justify-between flex-wrap gap-4" data-testid={`batch-header-${batch.id}`}>
        <div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <div className="font-display text-3xl font-bold tracking-tight" data-testid={`batch-title-${batch.id}`}>{batch.kind === "stock" ? `${batch.label || "📦 Batch order"} · ${batch.batch_date}` : `Batch · ${batch.batch_date}`}</div>
            <Badge className={`border ${meta.tone}`} data-testid={`batch-factory-badge-${batch.id}`}>{meta.label}</Badge>
          </div>
          <div className="text-base font-mono text-slate-700 mt-1">{fmt$(total)}</div>
          <div className="text-xs text-slate-500 mt-0.5">{orders.length} {batch.kind === "stock" ? "set(s) for stock" : "order(s)"} · {batch.paid ? "paid" : "unpaid"}</div>
        </div>
        <div className="flex items-center gap-3">
          {batch.paid ? (
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border px-3 py-1.5 text-sm" data-testid={`batch-paid-icon-${batch.id}`}>
              ✓ PAID · {batch.paid_at?.slice(0, 10)}
            </Badge>
          ) : isSuperAdmin ? (
            <Button data-testid={`batch-mark-paid-${batch.id}`} onClick={onPay} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              ✓ Sign &amp; Mark Paid
            </Button>
          ) : (
            <Badge className="bg-amber-50 text-amber-700 border-amber-200 border px-3 py-1.5 text-sm">Awaiting payment</Badge>
          )}
        </div>
      </div>

      {/* Spreadsheet */}
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid={`batch-sheet-${batch.id}`}>
          <thead className="bg-slate-100">
            <tr>
              <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase tracking-wider border-r border-slate-200">#</th>
              <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase tracking-wider border-r border-slate-200">
                <PoSortHeader sort={poSort} onChange={setPoSort} testid="batch-po-sort" />
              </th>
              {["Catalog", ...(batch.kind === "stock" ? ["Colour · Codes"] : []), "Vehicle", "Status", "ETA", "Price", "Line Total", ...(canRequestRefund ? ["Action"] : [])].map((h) => (
                <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase tracking-wider border-r border-slate-200 last:border-r-0">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedOrders.map((o, i) => (
              <tr key={o.id} className="border-t border-slate-200" data-testid={`sheet-row-${o.id}`}>
                <td className="px-3 py-2 text-slate-500 font-mono border-r border-slate-100">{i + 1}</td>
                <td className="px-3 py-2 font-mono font-semibold border-r border-slate-100">{o.po_number}{o.is_stock_unit && <span className="ml-2 inline-block rounded border border-indigo-300 bg-indigo-50 px-1.5 py-0.5 text-[10px] font-sans font-semibold text-indigo-800 align-middle" title={`Stock unit — batch order #${o.stock_batch_no}`} data-testid={`stock-unit-${o.id}`}>📦 STOCK #{o.stock_batch_no}</span>}{o.half_set && <span className="ml-2 inline-block rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-sans font-semibold text-amber-800 align-middle" data-testid={`half-set-${o.id}`}>½ SET</span>}</td>
                <td className="px-3 py-2 font-mono text-xs text-slate-600 border-r border-slate-100">{o.catalog_number || "—"}</td>
                {batch.kind === "stock" && (
                  <td className="px-3 py-2 font-mono text-xs text-slate-600 border-r border-slate-100 whitespace-nowrap" data-testid={`sheet-codes-${o.id}`}>
                    <div className="text-slate-900">{o.color || "—"} · {({ china: "🇨🇳", canada: "🇨🇦", usa: "🇺🇸" })[o.stock_dest] || ""} {o.stock_dest || ""}</div>
                    <div>F {o.front_cover_code || "—"}{o.f_clamp_code ? ` ⟟ ${o.f_clamp_code}` : ""} · R {o.rear_cover_code || "—"}{o.r_clamp_code ? ` ⟟ ${o.r_clamp_code}` : ""}</div>
                  </td>
                )}
                <td className="px-3 py-2 text-slate-700 border-r border-slate-100">{[o.brand, o.model].filter(Boolean).join(" · ") || "—"}</td>
                <td className="px-3 py-2 border-r border-slate-100">
                  {(() => {
                    const st = statusForRow(o);
                    return (
                      <span className={"inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border " + (BADGE_CLS[st.badge] || BADGE_CLS.neutral)}>
                        {st.label}
                      </span>
                    );
                  })()}
                </td>
                <td className="px-3 py-2 font-mono text-xs border-r border-slate-100">
                  {(isFactory && !isSuperAdmin ? FACTORY_DONE_STAGES.has(o.status) : o.status === "completed")
                    ? <span className="text-emerald-700">✓ {(o.packing_received_at || o.completed_at)?.slice(0, 10)}</span>
                    : o.production_estimated_completion
                      ? <span className="text-red-700">delayed: {o.production_estimated_completion}</span>
                      : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2 border-r border-slate-100" data-testid={`sheet-price-${o.id}`}>
                  {batch.paid ? (
                    <span className="font-mono">{fmt$(o.price)}</span>
                  ) : editing === o.id ? (
                    <div className="flex gap-1 items-center">
                      <Input
                        data-testid={`sheet-price-input-${o.id}`}
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(null); }}
                        onBlur={commit}
                        autoFocus
                        className="h-8 w-24 text-sm"
                      />
                    </div>
                  ) : (
                    <button
                      className="text-left w-full font-mono hover:bg-slate-100 rounded px-1 py-0.5"
                      onClick={() => beginEdit(o)}
                      data-testid={`sheet-price-btn-${o.id}`}
                    >
                      {fmt$(o.price)} <span className="text-slate-400 text-[10px] ml-1">✎</span>
                    </button>
                  )}
                </td>
                <td className="px-3 py-2 font-mono font-semibold">{fmt$(o.price)}</td>
                {canRequestRefund && (
                  <td className="px-3 py-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-50"
                      onClick={() => onRequestRefund(o)}
                      data-testid={`sheet-refund-btn-${o.id}`}
                    >
                      Refund
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-50">
            <tr>
              <td colSpan={6} className="px-3 py-2 text-right font-semibold text-slate-600 uppercase text-xs tracking-widest">Total</td>
              <td className="px-3 py-2" />
              <td className="px-3 py-2 font-display text-lg font-bold">{fmt$(total)}</td>
              {canRequestRefund && <td />}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
